<template>
  <div :id="`${source}_prompt_email`" class="px-2 truncate">
    <a-tooltip v-if="row?.email" :title="row?.email">
      <span class="text-sm truncate">{{ row.email }}</span>
    </a-tooltip>
    <div v-else class="px-2">
      <a-button
        class="secondary_medium"
        @click="accessEmail"
        :loading="isEnriching"
      >
        <ThemeImage
          v-if="!isEnriching"
          :icon="imageNames.PROSPECT_SEARCH_ICON"
          alt="prospect_search_icon"
          class="size-4"
        />
        {{ buttonText }}
      </a-button>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from "vue";
import { message } from "ant-design-vue";
import { imageNames } from "@/src/constants/imageConstants.js";
import DashboardService from "@/src/api/DashboardService.js";
import eventBus from "@/src/utils/eventBus.js";
import { quickActionsTypes } from "@/src/constants/componentConstants.js";
import { enrichWithApollo } from "@/src/utils/enrichUtils.js";
import { entityTypes } from "@/src/constants/entityTypeConstants.js";
import { showAlreadyExistsMessage } from "@/src/utils/utils.js";
import { handleApiError } from "@/src/utils/errorUtils.js";

const props = defineProps({
  source: { type: String, required: true, default: "custom_table" },
  row: { type: Object, required: true },
});

const isEnriching = ref(false);
const entity = ref(null);
const actionKey = ref("email");

const buttonText = computed(() => {
  return isEnriching.value ? "Accessing..." : "Access Email";
});

const enrichingEventName = computed(() => `email-enriching-${props.row.id}`);

const setEnrichingState = (state) => {
  isEnriching.value = state;
  eventBus.emit(enrichingEventName.value, state);
};

const accessEmail = async () => {
  if (!props.row?.lead_id && !props.row?.contact_id) {
    await addToLeads();
  } else {
    await enrichEntity();
  }
};

const handleRateLimitError = (error) => {
  handleApiError(error, message, (errorMessage) => {
    showAlreadyExistsMessage({ customMessage: errorMessage });
  });
};

const handleGenericError = (error) => {
  handleApiError(error, message, (errorMessage) => {
    showAlreadyExistsMessage({ customMessage: errorMessage });
  });
};

const processEmailResponse = (response) => {
  entity.value = response.lead;

  if (entity.value && response.lead.email) {
    entity.value[actionKey.value] = response.lead.email;
  }

  eventBus.emit("table-row-updated", {
    ...response,
    action: quickActionsTypes.EMAIL,
    id: props.row.id,
  });
};

const handleSuccessResponse = (response) => {
  if (!response.lead?.email) {
    message.info("Email not available");
    return;
  }

  processEmailResponse(response);
};

const handleFailureResponse = (response) => {
  const errorMessage =
    response?.message ||
    response?.error ||
    "Error occurred while fetching email";
  showAlreadyExistsMessage({ customMessage: errorMessage });
};

const addToLeads = async () => {
  setEnrichingState(true);

  const payload = {
    target: 1,
    list_id: null,
    sequence_id: null,
    selected_id: entity.value?.id,
    reveal_phone_number: false,
    reveal_email: true,
  };

  try {
    const response = await DashboardService.enrichAndAddLead(payload);

    if (response?.success) {
      handleSuccessResponse(response);
    } else {
      handleFailureResponse(response);
    }
  } catch (error) {
    handleApiError(error, message, (errorMessage) => {
      showAlreadyExistsMessage({ customMessage: errorMessage });
    });
  } finally {
    setEnrichingState(false);
  }
};

const determineEntitySource = () => {
  if (props.row?.contact_id) {
    return entityTypes.CONTACT;
  }

  if (props.row?.lead_id) {
    return entityTypes.LEAD;
  }

  return props.source;
};

const getEntityId = () => {
  return props.row.lead_id || props.row.contact_id;
};

const processEnrichmentResponse = (response) => {
  entity.value = response;

  if (entity.value && response.email) {
    entity.value[actionKey.value] = response.email;
  }

  eventBus.emit("table-row-updated", {
    ...response,
    action: quickActionsTypes.EMAIL,
    id: props.row.id,
  });
};

const handleEnrichmentSuccess = (response) => {
  if (!response?.email) {
    message.info("Email not available");
    return;
  }

  processEnrichmentResponse(response);
};

const handleEnrichmentError = (error) => {
  handleApiError(error, message, (errorMessage) => {
    showAlreadyExistsMessage({ customMessage: errorMessage });
  });
};

const enrichEntity = async () => {
  setEnrichingState(true);

  const source = determineEntitySource();
  const entityId = getEntityId();
  const enrichmentOptions = {
    revealEmails: true,
    revealPhoneNumbers: false,
    includeRequestId: false,
  };

  try {
    const response = await enrichWithApollo(
      entityId,
      source,
      enrichmentOptions.revealEmails,
      enrichmentOptions.revealPhoneNumbers,
      enrichmentOptions.includeRequestId,
    );

    handleEnrichmentSuccess(response);
  } catch (error) {
    handleEnrichmentError(error);
  } finally {
    setEnrichingState(false);
  }
};

const handleEnrichingEvent = (state) => {
  isEnriching.value = state;
};

watch(
  () => props.row,
  (newRow) => {
    if (newRow) {
      entity.value = newRow;
      if (newRow.email && entity.value) {
        entity.value[actionKey.value] = newRow.email;
      }
    }
  },
  { deep: true, immediate: true },
);

onMounted(() => {
  entity.value = props.row;
  eventBus.on(enrichingEventName.value, handleEnrichingEvent);
});

onUnmounted(() => {
  eventBus.off(enrichingEventName.value, handleEnrichingEvent);
});
</script>
